import { LogEntry } from "./log-entry.model";
import { pickIdsToEvict, RotationLimits } from "./log-rotation";
import { LOGS_STORE_NAME, LOGS_TIMESTAMP_INDEX_NAME } from "../storage/log-db.config";
import { IndexedDbConfig } from "../storage/indexed-db.config";

const DEFAULT_MAX_ENTRIES = 3000;
const DEFAULT_MAX_AGE_DAYS = 2;

/**
 * Framework-free (no Angular DI/RxJS) buffer + `gciins-logs-db` writer, owned directly by
 * `LogAggregatorClient` — the only consumer since the Web Worker layer was removed (see
 * Docs/app-logs.md#post-implementation-notes).
 *
 * Every capture-path operation (`flush()`, `query()`) degrades gracefully: a storage failure is
 * swallowed rather than thrown, since logging must never be the reason the app breaks. `clear()`
 * is the one exception — it rejects on a real failure so the manual, user-confirmed "Clear" action
 * can report it truthfully instead of falsely claiming success (see the method's own doc).
 *
 * @see Docs/app-logs.md#aggregator--rotation-design
 */
export class LogStoreWriter {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private buffer: LogEntry[] = [];
  private limits: RotationLimits = { maxEntries: DEFAULT_MAX_ENTRIES, maxAgeDays: DEFAULT_MAX_AGE_DAYS };

  constructor(private readonly logsDbConfig: IndexedDbConfig) {}

  configure(limits: RotationLimits): void {
    this.limits = limits;
  }

  enqueue(entry: LogEntry): void {
    this.buffer.push(entry);
  }

  get bufferSize(): number {
    return this.buffer.length;
  }

  /** Writes the current buffer to `gciins-logs-db`, then runs the rotation check. */
  async flush(): Promise<void> {
    if (this.buffer.length === 0) {
      return;
    }
    const batch = this.buffer;
    this.buffer = [];
    try {
      const db = await this.openDb();
      await this.putAll(db, batch);
      // Runs on every flush (not throttled to every Nth one) so the store never transiently
      // overshoots `limits.maxEntries` between checks. The scan only loads {id, timestamp} pairs
      // via the timestamp index's key cursor (see `fetchAllMeta()`), and IndexedDB's own API is
      // asynchronous, so this never blocks the main thread.
      await this.runRotation(db);
    } catch {
      // Degrade gracefully — a dropped batch is preferable to a broken app.
    }
  }

  /** Returns the most recent `limit` entries, newest first. */
  async query(limit: number): Promise<LogEntry[]> {
    try {
      const db = await this.openDb();
      return await this.queryRecent(db, limit);
    } catch {
      return [];
    }
  }

  /**
   * Drops the in-memory buffer and clears every entry from `gciins-logs-db`.
   *
   * Unlike `flush()`/`query()`, failures are **not** swallowed here: the manual, user-confirmed
   * "Clear" action on the log viewer page (`AppLogs.clearLogs()`) must be able to tell a real
   * failure apart from success and show an error state instead of falsely reporting that the
   * logs were deleted.
   */
  async clear(): Promise<void> {
    this.buffer = [];
    const db = await this.openDb();
    await this.clearStore(db);
  }

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.logsDbConfig.dbName, this.logsDbConfig.version);
      req.onupgradeneeded = event => {
        const db = req.result;
        const tx = (event.target as IDBOpenDBRequest).transaction!;
        for (const storeConfig of this.logsDbConfig.stores) {
          const store = db.objectStoreNames.contains(storeConfig.name)
            ? tx.objectStore(storeConfig.name)
            : db.createObjectStore(storeConfig.name, { keyPath: storeConfig.keyPath, autoIncrement: true });
          for (const indexConfig of storeConfig.indexes ?? []) {
            if (!store.indexNames.contains(indexConfig.name)) {
              store.createIndex(indexConfig.name, indexConfig.keyPath, { unique: indexConfig.unique ?? false });
            }
          }
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        this.dbPromise = null;
        reject(req.error);
      };
    });
    return this.dbPromise;
  }

  private putAll(db: IDBDatabase, entries: LogEntry[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LOGS_STORE_NAME, "readwrite");
      const store = tx.objectStore(LOGS_STORE_NAME);
      for (const entry of entries) {
        store.put(entry);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Log write transaction failed"));
      tx.onabort = () => reject(tx.error ?? new Error("Log write transaction aborted"));
    });
  }

  private async runRotation(db: IDBDatabase): Promise<void> {
    const allMeta = await this.fetchAllMeta(db);
    const idsToEvict = pickIdsToEvict(allMeta, this.limits);
    if (idsToEvict.length === 0) {
      return;
    }
    await this.deleteByIds(db, idsToEvict);
  }

  /**
   * Metadata-only rotation scan: reads `{id, timestamp}` pairs via the `timestamp` index's key
   * cursor, so every retained log's full `message`/`context`/`stack` never gets structured-cloned
   * into the main thread — only the index's key (`timestamp`) and primary key (`id`) are.
   */
  private fetchAllMeta(db: IDBDatabase): Promise<{ id: number; timestamp: number }[]> {
    return new Promise((resolve, reject) => {
      const meta: { id: number; timestamp: number }[] = [];
      const index = db.transaction(LOGS_STORE_NAME, "readonly").objectStore(LOGS_STORE_NAME).index(LOGS_TIMESTAMP_INDEX_NAME);
      const cursorReq = index.openKeyCursor();
      cursorReq.onsuccess = event => {
        const cursor = (event.target as IDBRequest<IDBCursor>).result;
        if (cursor) {
          meta.push({ id: cursor.primaryKey as number, timestamp: cursor.key as number });
          cursor.continue();
        } else {
          resolve(meta);
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
  }

  private deleteByIds(db: IDBDatabase, ids: number[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LOGS_STORE_NAME, "readwrite");
      const store = tx.objectStore(LOGS_STORE_NAME);
      for (const id of ids) {
        store.delete(id);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private clearStore(db: IDBDatabase): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LOGS_STORE_NAME, "readwrite");
      tx.objectStore(LOGS_STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private queryRecent(db: IDBDatabase, limit: number): Promise<LogEntry[]> {
    return new Promise((resolve, reject) => {
      const results: LogEntry[] = [];
      const cursorReq = db.transaction(LOGS_STORE_NAME, "readonly").objectStore(LOGS_STORE_NAME).openCursor(null, "prev");
      cursorReq.onsuccess = event => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor && results.length < limit) {
          results.push(cursor.value as LogEntry);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
  }
}
